import { StyleSheet, Text, View } from 'react-native';
import React, { FC, useCallback, useMemo } from 'react';
import { STYLE_VARS } from '../../../constants/style-vars';
import { ErrorMessage, Field, FieldProps, Formik } from 'formik';
import {
  FormComponent,
  FormStructure,
  IFormData,
} from '../../../components/FormHelper/FormHelper.types';
import { TextInput } from '../../../components/TextInput/TextInput';
import { Button } from '../../../components/Button';
import { useIsDesktop } from '../../../hooks/useIsDeskTop';
import { COLORS, FONTS } from '../../../constants';
import DropDown from '../../../components/DropDown/DropDown';
import { DropDownItem } from '../../../models/DropDownItem';
import { IS_WEB } from '../../../constants/platform';
import * as yup from 'yup';

interface EventSignUpFormProps {
  onSubmitHandler?: (data: IFormData) => void;
}

const EventSignUpForm: FC<EventSignUpFormProps> = ({ onSubmitHandler }) => {
  const isDesktop = useIsDesktop();
  const initData = useMemo(() => {
    return {
      nAdults: '',
      nChildren: '',
      special: '',
    };
  }, []);

  const numberSelect = useCallback((name: string) => {
    const arr: DropDownItem[] = [
      {
        label: 'No ' + name,
        value: 0,
      },
    ];
    for (let i = 1; i <= 100; i++) {
      arr.push({
        label: i + ' ' + name,
        value: i,
      });
    }
    return arr;
  }, []);

  const signUpStructure = useMemo(() => {
    const structure: FormStructure = {
      components: [
        {
          name: 'nAdults',
          placeholder: 'NO. OF ADULTS',
          type: 'dropdown',
          items: numberSelect('adults'),
          zIndex: 11,
        },
        {
          name: 'nChildren',
          placeholder: 'NO. OF CHILDREN',
          type: 'dropdown',
          items: numberSelect('children'),
          zIndex: 10,
        },
        {
          name: 'special',
          placeholder: 'SPECIAL REQUEST',
          type: 'text',
        },
      ],
    };
    return structure;
  }, [numberSelect]);

  const renderForm = useCallback((component: FormComponent) => {
    switch (component.type) {
      case 'dropdown':
        return (
          <Field name={component.name} key={component.name}>
            {({ field }: FieldProps) => (
              <View style={{}}>
                <DropDown
                  items={component.items || []}
                  placeholder={component.placeholder || 'SELECT'}
                  customDownButton={require('../../../assets/down-arrow.png')}
                  customUpButton={require('../../../assets/up-arrow.png')}
                  zIndex={component.zIndex}
                  dropdownStyle={{
                    borderColor: COLORS.DARK_GREY,
                  }}
                  textStyle={{
                    color: COLORS.DARKER_GREY,
                  }}
                  dropDownContainerTextStyle={{
                    color: COLORS.DARKER_GREY,
                  }}
                  buttonStyle={{
                    tintColor: COLORS.DARKER_GREY,
                  }}
                  setValues={(value) => {
                    field.onChange(field.name)(value.value.toString());
                  }}
                />
                <ErrorMessage
                  name={field.name}
                  render={(errorMessage: string) => (
                    <Text style={{ color: 'red', marginTop: 5 }}>
                      {errorMessage}
                    </Text>
                  )}
                />
              </View>
            )}
          </Field>
        );
      case 'text':
        return (
          <Field key={component.name} name={component.name}>
            {({ field }: FieldProps) => (
              <View
                style={{
                  borderWidth: 0.5,
                  borderColor: IS_WEB ? COLORS.DARK_GREY : COLORS.DARKER_GREY,
                }}
                key={component.name}
              >
                <TextInput
                  placeholder={component.placeholder || 'Input ...'}
                  onChangeText={field.onChange(field.name)}
                  value={field.value}
                  id={field.name}
                  placeholderTextColor={STYLE_VARS.fccDarkGrey}
                  style={styles.inputDesktop}
                />
              </View>
            )}
          </Field>
        );
      default:
        return <></>;
    }
  }, []);

  if (isDesktop()) {
    return (
      <View style={{ marginTop: 20 }}>
        <Text
          style={{
            fontFamily: FONTS.IowanOldStyleBTRoman,
            fontSize: 24,
            fontWeight: '500',
            color: STYLE_VARS.textColor,
            textTransform: 'uppercase',
            marginBottom: 25,
          }}
        >
          Sign Up
        </Text>
        <View style={{ flexDirection: 'row' }}>
          <View style={{ width: '50%' }}>
            <Formik
              initialValues={initData}
              onSubmit={(value) => {
                if (onSubmitHandler) onSubmitHandler(value);
              }}
              validateOnBlur={true}
              validationSchema={yup.object().shape({
                nAdults: yup.number().required('Please Select No. Of Adults'),
                nChildren: yup
                  .number()
                  .required('Please Select No. Of Children'),
                special: yup.string(),
              })}
            >
              {({ handleSubmit }) => (
                <View>
                  {signUpStructure.components.map((component) => (
                    <View
                      style={{
                        zIndex: component.zIndex,
                        marginBottom: 10,
                      }}
                    >
                      {renderForm(component)}
                    </View>
                  ))}
                  <View style={{ flexDirection: 'row' }}>
                    <Button
                      text="SUBMIT"
                      style={STYLE_VARS.blueButton}
                      onPress={() => {
                        handleSubmit();
                      }}
                    />
                  </View>
                </View>
              )}
            </Formik>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View>
      <Text
        style={{
          textAlign: 'center',
          fontFamily: FONTS.IowanOldStyleBTRoman,
          fontSize: 16,
          fontWeight: '500',
          color: STYLE_VARS.textColor,
          textTransform: 'uppercase',
        }}
      >
        Sign Up
      </Text>
      <Formik
        initialValues={initData}
        onSubmit={(value) => {
          if (onSubmitHandler) onSubmitHandler(value);
        }}
        validateOnBlur={true}
      >
        {({ handleSubmit }) => (
          <View
            style={{
              marginVertical: 20,
            }}
          >
            {signUpStructure.components.map((component) => (
              <View
                style={{
                  zIndex: component.zIndex,
                  marginBottom: 10,
                }}
              >
                {renderForm(component)}
              </View>
            ))}
            <View style={{ marginTop: 10 }}>
              <Button
                text="SUBMIT"
                style={STYLE_VARS.blueButton}
                onPress={() => {
                  handleSubmit();
                }}
              />
            </View>
          </View>
        )}
      </Formik>
    </View>
  );
};

export default EventSignUpForm;

const styles = StyleSheet.create({
  input: {
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: STYLE_VARS.fccDarkGrey,
    fontFamily: FONTS.IowanOldStyleBTRoman,
    fontSize: 14,
    fontWeight: '500',
    color: STYLE_VARS.fccDarkerGrey,
  },
  inputDesktop: {
    height: 40,
    paddingHorizontal: 20,
    alignItems: 'flex-start',
    paddingVertical: 10,
    color: STYLE_VARS.fccDarkerGrey,
    fontSize: 12,
    letterSpacing: 0.8,
    fontWeight: '500',
    fontFamily: FONTS.IowanOldStyleBTRoman,
  },
});
